import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import type { TrpcClient } from '@client/hooks/api/useTrpc';
import { downloadBlob, sanitiseExportText, splitUrlLinkArray } from '@client/utils';
import { DateTime } from 'luxon';
import invariant from 'tiny-invariant';

type ObligationPayload = Awaited<ReturnType<TrpcClient['obligation']['getObligation']['query']>>;

export type ObligationExportRow = { Field: string; Value: string };

const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return '';
    const dt =
        date instanceof Date ? DateTime.fromJSDate(date, { zone: 'utc' }) : DateTime.fromISO(date, { zone: 'utc' });
    return dt.isValid ? dt.toFormat('d/M/yyyy') : '';
};

const yesNo = (value: boolean | null | undefined): string => (value ? 'Yes' : 'No');

export function buildObligationExportRows(
    obligation: ObligationPayload,
    ownerName: string | null,
): ObligationExportRow[] {
    const rows: ObligationExportRow[] = [];
    const push = (field: string, value: string | null | undefined) => {
        rows.push({ Field: field, Value: sanitiseExportText(value ?? '') });
    };
    const pushRaw = (field: string, value: string) => {
        rows.push({ Field: field, Value: value });
    };
    const pushArray = (field: string, values: ReadonlyArray<string | null | undefined>) => {
        values.forEach((v, i) => push(`${field} / ${i}`, v ?? ''));
    };
    const pushLinkArray = (urlField: string, nameField: string, raw: unknown) => {
        const { names, urls } = splitUrlLinkArray(raw);
        urls.forEach((url, i) => {
            pushRaw(`${urlField} / ${i}`, url);
            pushRaw(`${nameField} / ${i}`, names[i] ?? '');
        });
    };

    push('Identification code', obligation.identificationCode);
    push('Legislation', obligation.Legislation?.name);
    pushRaw('Effective date from', formatDate(obligation.effectiveDateFrom));
    pushRaw('Effective date to', obligation.effectiveDateTo ? formatDate(obligation.effectiveDateTo) : 'Ongoing');
    push('Core obligation', obligation.coreObligation);
    push('What it means', obligation.whatItMeans);
    push('How we are complying', obligation.howWeAreComplying);
    push('Section', obligation.section);
    push('Source', obligation.source);
    push('Legislation description', obligation.legislationDescription);
    pushLinkArray('Austlii link URL', 'Austlii link Name', obligation.austLIIURLs);
    pushLinkArray(
        'Federal register of legislation link URL',
        'Federal register of legislation link Name',
        obligation.federalRegisterOfLegislationURLs,
    );
    pushArray('Master breach examples', obligation.masterBreachExamples ?? []);
    pushArray('Breach examples', obligation.breachExamples ?? []);
    push('Regulatory guidance', obligation.regulatoryGuidance);
    push('Regulator', obligation.regulator);
    push('Regulatory guidance description', obligation.regulatoryGuidanceDescription);
    pushLinkArray('Regulatory guidance link URL', 'Regulatory guidance link Name', obligation.regulatoryGuidanceURLs);
    pushArray(
        'Control',
        obligation.Controls.map((c) => c.Control.name),
    );
    push('Status', obligation.status);
    push('Country', obligation.country);
    const handlingParties = obligation.HandlingParties.map((hp) => sanitiseExportText(hp.HandlingParty.name))
        .filter(Boolean)
        .join('; ');
    pushRaw('Handling parties', handlingParties);
    pushRaw('Owner name', ownerName ? sanitiseExportText(ownerName) : 'Unallocated');
    pushRaw('Version', obligation.version != null ? String(obligation.version) : '');
    pushRaw('Was published', yesNo(obligation.wasPublished));
    pushRaw('Is inherited', yesNo(obligation.isInherited));

    return rows;
}

async function lookupOwnerName(trpcClient: TrpcClient, ownerId: bigint | null | undefined): Promise<string | null> {
    if (ownerId == null) return null;
    try {
        const { rows } = await trpcClient.user.listUsersForSelector.query({ where: { userId: ownerId } });
        return rows[0]?.fullName ?? null;
    } catch {
        return null;
    }
}

export async function exportObligation(trpcClient: TrpcClient, obligationId: bigint) {
    try {
        message.loading('Generating file. Please wait...', 0);
        const obligation = await trpcClient.obligation.getObligation.query({ id: obligationId });
        invariant(obligation, 'Obligation not found');

        const ownerName = await lookupOwnerName(trpcClient, obligation.ownerId);
        const rows = buildObligationExportRows(obligation, ownerName);

        const { parse } = await import('json2csv');
        const csv = parse(rows, { fields: ['Field', 'Value'] });
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        downloadBlob(blob, `obligation-${obligation.id}-${new Date().toISOString()}.csv`);
        message.success('File saved.');
    } catch (e) {
        message.destroy();
        errorMessage.show(e);
    }
}
